// Issue: Inline fix for org switcher z value:

import React, { ReactNode, ComponentType } from "react";
import { Navbar, Alignment, AnchorButton, Button } from "@blueprintjs/core";
import { useNavigate } from "react-router-dom";
import { Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useProjects } from "../auth/ProjectContext";
import RightSidebar from "./RightSidebar";
import PrimarySidebar from "./PrimarySidebar";
import Footer from "./Footer";
import OrgSwitcher from "./OrgSwitcher";
import ProjectSwitcher from "./ProjectSwitcher";

const NAVBAR_HEIGHT = 50;
const SUBHEADER_HEIGHT = 60;
const FOOTER_HEIGHT = 40;
const LEFT_WIDTH = 200;
const RAIL_WIDTH = 60;

interface MainLayoutProps {
  children?: ReactNode;
  pageTitle: string;
  LeftBar: ComponentType;
}

export default function MainLayout({
  children,
  pageTitle,
  LeftBar,
}: MainLayoutProps) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { currentProject } = useProjects();
  const location = useLocation();
  const CONTENT_LEFT = RAIL_WIDTH + LEFT_WIDTH;

  const hideSwitchers = location.pathname.startsWith("/account");
  const dynamicTitle =
    (location.pathname.startsWith("/account/projects/") ||
      location.pathname.startsWith("/projects/")) && currentProject
      ? currentProject.name
      : pageTitle;

  return (
    <>
      <Navbar
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: NAVBAR_HEIGHT,
          zIndex: 1000,
          borderRadius: 0,
        }}
      >
        <Navbar.Group align={Alignment.LEFT}>
          <img
            src={`${process.env.PUBLIC_URL}/ee-design/images/ee-01.png`}
            alt="Endeavor Elements Logo"
            style={{ width: 32, marginRight: "0.5rem" }}
          />
          <Navbar.Heading>ConformIQ</Navbar.Heading>
          <Navbar.Divider />
          <AnchorButton
            className="bp4-minimal"
            icon="dashboard"
            text="Dashboard"
            onClick={() => navigate("/dashboard")}
          />
          {!hideSwitchers && (
            <>
              <OrgSwitcher />
              <ProjectSwitcher />
            </>
          )}
        </Navbar.Group>

        <Navbar.Group align={Alignment.RIGHT} style={{ zIndex: 1100 }}>
          <Navbar.Divider />
          <AnchorButton
            className="bp4-minimal"
            icon="user"
            text="Account"
            onClick={() => navigate("/account")}
          />
          <Navbar.Divider />
          <Button
            className="bp4-minimal"
            icon="log-out"
            text="Logout"
            style={{ marginLeft: "0.25rem" }}
            onClick={logout}
          />
        </Navbar.Group>
      </Navbar>

      <header
        style={{
          position: "fixed",
          top: NAVBAR_HEIGHT,
          left: RAIL_WIDTH,
          right: RAIL_WIDTH,
          height: SUBHEADER_HEIGHT,
          background: "#fff",
          borderBottom: "1px solid #d8e1e8",
          display: "flex",
          alignItems: "center",
          padding: "0 1rem",
          zIndex: 900, // ↓ lowered so popover renders above
        }}
      >
        <h1 style={{ margin: 0, fontSize: "1.5rem" }}>{dynamicTitle}</h1>
      </header>

      <aside
        style={{
          position: "fixed",
          top: NAVBAR_HEIGHT,
          bottom: FOOTER_HEIGHT,
          left: 0,
          width: RAIL_WIDTH,
          background: "#f5f8fa",
          borderRight: "1px solid #d8e1e8",
          overflowY: "auto",
        }}
      >
        <PrimarySidebar />
      </aside>

      <aside
        style={{
          position: "fixed",
          top: NAVBAR_HEIGHT + SUBHEADER_HEIGHT,
          bottom: FOOTER_HEIGHT,
          left: RAIL_WIDTH,
          width: LEFT_WIDTH,
          background: "#f5f8fa",
          borderRight: "1px solid #d8e1e8",
          overflowY: "auto",
        }}
      >
        <LeftBar />
      </aside>

      <aside
        style={{
          position: "fixed",
          top: NAVBAR_HEIGHT,
          bottom: FOOTER_HEIGHT,
          right: 0,
          width: RAIL_WIDTH,
          background: "#f5f8fa",
          borderLeft: "1px solid #d8e1e8",
          overflowY: "auto",
        }}
      >
        <RightSidebar />
      </aside>

      <main
        style={{
          marginTop: NAVBAR_HEIGHT + SUBHEADER_HEIGHT,
          marginBottom: FOOTER_HEIGHT,
          marginLeft: CONTENT_LEFT,
          marginRight: RAIL_WIDTH,
          minHeight: `calc(100vh - ${NAVBAR_HEIGHT + SUBHEADER_HEIGHT + FOOTER_HEIGHT}px)`,
          padding: "2rem",
        }}
      >
        {children ?? <Outlet />}
      </main>

      <footer
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          height: FOOTER_HEIGHT,
          background: "#f5f8fa",
          borderTop: "1px solid #d8e1e8",
          display: "flex",
          alignItems: "center",
          padding: "0 1rem",
          fontSize: "0.85rem",
          color: "#777",
          zIndex: 1000,
        }}
      >
        <Footer />
      </footer>
    </>
  );
}
